/**
 * LogTape-based Logging for Poker Reborn
 *
 * Usage:
 *   import { log } from '@/utils/log';
 *   log.network.info`Connected to ${url}`;
 *   log.table.debug`Player action: ${action}`;
 *
 * Runtime control (browser console):
 *   pokerLog.status()           // See available commands
 *   pokerLog.enable('NETWORK')  // Enable category
 *   pokerLog.disable('PING')    // Disable category
 *   pokerLog.enableAll()        // Enable all
 *   pokerLog.disableAll()       // Disable all (errors only)
 */

import {
  configure,
  getConsoleSink,
  getLogger,
  type Logger,
} from '@logtape/logtape';

// =============================================================================
// CATEGORY CONSTANTS
// =============================================================================

export const LOG = {
  POKER: ['poker'],
  NETWORK: ['poker', 'network'],
  PING: ['poker', 'network', 'ping'],
  TABLE: ['poker', 'table'],
  GAME: ['poker', 'game'],
  LOBBY: ['poker', 'lobby'],
  ACCOUNT: ['poker', 'account'],
  ASSETS: ['poker', 'assets'],
  AUDIO: ['poker', 'audio'],
  UI: ['poker', 'ui'],
  CARDS: ['poker', 'ui', 'cards'],
  GAMEPAD: ['poker', 'ui', 'gamepad'],
  STORAGE: ['poker', 'storage'],
  APP: ['poker', 'app'],
  TEST: ['poker', 'test'],
  EFFECTS: ['poker', 'effects'],
  INBOX: ['poker', 'inbox'],
  DAILY_BONUS: ['poker', 'dailybonus'],
  AVATAR: ['poker', 'avatar'],
  ODOO: ['poker', 'odoo'],
  XP: ['poker', 'xp'],
  GIFTS: ['poker', 'gifts'],
  AUTH: ['poker', 'auth'],
} as const;

export type LogCategory = (typeof LOG)[keyof typeof LOG];

// =============================================================================
// CONFIGURATION
// =============================================================================

let configured = false;

export async function setupLogging(): Promise<void> {
  if (configured) return;

  await configure({
    sinks: {
      console: getConsoleSink(),
    },
    loggers: [
      // Suppress LogTape meta logger info message
      {
        category: ['logtape', 'meta'],
        lowestLevel: 'warning',
      },
      // Root logger - default to warning only to reduce noise
      {
        category: ['poker'],
        sinks: ['console'],
        lowestLevel: 'warning',
      },
      // Game events - keep for debugging showdown
      {
        category: ['poker', 'game'],
        lowestLevel: 'info',
      },
      // Lobby - enable for Quick Play debugging
      {
        category: ['poker', 'lobby'],
        sinks: ['console'],
        lowestLevel: 'debug',
      },
      // Network - enable for message debugging
      {
        category: ['poker', 'network'],
        sinks: ['console'],
        lowestLevel: 'debug',
      },
      // Cards UI - keep for debugging card display
      {
        category: ['poker', 'ui', 'cards'],
        lowestLevel: 'debug',
      },
      // Suppress ping/pong noise (only show errors)
      {
        category: ['poker', 'network', 'ping'],
        lowestLevel: 'error',
      },
      // Inbox - enable for debugging inbox messages
      {
        category: ['poker', 'inbox'],
        lowestLevel: 'debug',
      },
      // Daily bonus - enable for debugging bonus flow
      {
        category: ['poker', 'dailybonus'],
        lowestLevel: 'debug',
      },
      // Avatar system
      {
        category: ['poker', 'avatar'],
        lowestLevel: 'debug',
      },
      // Odoo REST API
      {
        category: ['poker', 'odoo'],
        sinks: ['console'],
        lowestLevel: 'debug',
      },
      // XP / Level progression
      {
        category: ['poker', 'xp'],
        lowestLevel: 'debug',
      },
      // Gift system (T42)
      {
        category: ['poker', 'gifts'],
        lowestLevel: 'debug',
      },
      // Auth system (T72)
      {
        category: ['poker', 'auth'],
        lowestLevel: 'debug',
      },
      // Suppress test logs in production
      {
        category: ['poker', 'test'],
        lowestLevel: import.meta.env.DEV ? 'debug' : 'error',
      },
      // Effects logging - T12 complete, all creators now registered
      {
        category: ['poker', 'effects'],
        lowestLevel: 'warning',
      },
    ],
  });

  configured = true;

  // Expose runtime API for dev tools
  if (import.meta.env.DEV) {
    exposeDevAPI();
  }
}

// =============================================================================
// LOGGER FACTORY
// =============================================================================

export function getLog(category: LogCategory): Logger {
  return getLogger(category);
}

// Pre-created loggers for convenience
export const log = {
  network: getLogger(LOG.NETWORK),
  ping: getLogger(LOG.PING),
  table: getLogger(LOG.TABLE),
  game: getLogger(LOG.GAME),
  lobby: getLogger(LOG.LOBBY),
  account: getLogger(LOG.ACCOUNT),
  assets: getLogger(LOG.ASSETS),
  audio: getLogger(LOG.AUDIO),
  ui: getLogger(LOG.UI),
  cards: getLogger(LOG.CARDS),
  gamepad: getLogger(LOG.GAMEPAD),
  storage: getLogger(LOG.STORAGE),
  app: getLogger(LOG.APP),
  test: getLogger(LOG.TEST),
  effects: getLogger(LOG.EFFECTS),
  inbox: getLogger(LOG.INBOX),
  dailyBonus: getLogger(LOG.DAILY_BONUS),
  avatar: getLogger(LOG.AVATAR),
  odoo: getLogger(LOG.ODOO),
  xp: getLogger(LOG.XP),
  gifts: getLogger(LOG.GIFTS),
  auth: getLogger(LOG.AUTH),
};

// =============================================================================
// RUNTIME DEV API
// =============================================================================

interface PokerLogAPI {
  categories: typeof LOG;
  setLevel: (category: string, level: string) => Promise<void>;
  enable: (category: string) => Promise<void>;
  disable: (category: string) => Promise<void>;
  enableAll: () => Promise<void>;
  disableAll: () => Promise<void>;
  status: () => void;
}

function exposeDevAPI(): void {
  const api: PokerLogAPI = {
    categories: LOG,

    async setLevel(category: string, level: string) {
      const cat = LOG[category.toUpperCase() as keyof typeof LOG];
      if (!cat) {
        console.error(
          `Unknown category: ${category}. Use pokerLog.categories to see available.`
        );
        return;
      }
      await configure({
        reset: true,
        sinks: { console: getConsoleSink() },
        loggers: [
          { category: ['poker'], sinks: ['console'], lowestLevel: 'debug' },
          {
            category: [...cat],
            lowestLevel: level as 'debug' | 'info' | 'warning' | 'error',
          },
        ],
      });
      console.log(`Set ${category} to ${level}`);
    },

    async enable(category: string) {
      await api.setLevel(category, 'debug');
    },

    async disable(category: string) {
      await api.setLevel(category, 'error');
    },

    async enableAll() {
      await configure({
        reset: true,
        sinks: { console: getConsoleSink() },
        loggers: [
          { category: ['poker'], sinks: ['console'], lowestLevel: 'debug' },
        ],
      });
      console.log('All logging enabled');
    },

    async disableAll() {
      await configure({
        reset: true,
        sinks: { console: getConsoleSink() },
        loggers: [
          { category: ['poker'], sinks: ['console'], lowestLevel: 'error' },
        ],
      });
      console.log('All logging disabled (errors only)');
    },

    status() {
      console.log('Available categories:', Object.keys(LOG));
      console.log('Usage:');
      console.log("  pokerLog.enable('NETWORK')");
      console.log("  pokerLog.disable('PING')");
      console.log("  pokerLog.setLevel('TABLE', 'warning')");
      console.log('  pokerLog.enableAll()');
      console.log('  pokerLog.disableAll()');
    },
  };

  (window as unknown as { pokerLog: PokerLogAPI }).pokerLog = api;
  console.log('pokerLog API ready. Type pokerLog.status() for help.');
}
